import { Injectable } from '@angular/core';
import { openDB, IDBPDatabase } from 'idb';

@Injectable({
  providedIn: 'root',
})
export class TidyStorage {
  private db: IDBPDatabase | null = null;
  private isInitializing = false;

  constructor() {}

  async init() {
    if (this.isInitializing) {
      // Wait for ongoing initialization to complete
      while (this.isInitializing) {
        await new Promise((resolve) => setTimeout(resolve, 10));
      }
      return;
    }

    this.isInitializing = true;
    try {
      this.db = await openDB('TidyStorageDB', 1, {
        upgrade(db) {
          db.createObjectStore('keyvaluepairs');
        },
      });
    } finally {
      this.isInitializing = false;
    }
  }

  private async ensureConnection(): Promise<void> {
    if (!this.db) {
      await this.init();
    }
  }

  private async executeWithRetry<T>(operation: () => Promise<T>): Promise<T> {
    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        await this.ensureConnection();
        return await operation();
      } catch (error) {
        lastError = error as Error;

        // If it's a database connection error, try to reinitialize
        if (
          error instanceof Error &&
          (error.message.includes('database connection is closing') ||
            error.message.includes('Database not initialized') ||
            error.message.includes('The database connection is closing') ||
            error.message.includes('failed to execute') ||
            error.message.includes('transaction'))
        ) {
          console.warn(
            `Database connection error detected, attempting retry ${
              attempt + 1
            }/${maxRetries}:`,
            error.message
          );

          // Reset the database connection
          this.db = null;

          // Wait a bit before retrying
          await new Promise((resolve) =>
            setTimeout(resolve, 100 * (attempt + 1))
          );
          continue;
        }

        // For other errors, don't retry
        throw error;
      }
    }

    console.error('Database operation failed after all retries:', lastError);
    throw lastError || new Error('Database operation failed after retries');
  }

  async save(key: string, value: any): Promise<void> {
    return this.executeWithRetry(async () => {
      if (!this.db) throw new Error('Database not initialized');
      await this.db.put('keyvaluepairs', value, key);
    });
  }

  async retrieve(key: string): Promise<any> {
    return this.executeWithRetry(async () => {
      if (!this.db) throw new Error('Database not initialized');
      return await this.db.get('keyvaluepairs', key);
    });
  }

  async delete(key: string): Promise<void> {
    return this.executeWithRetry(async () => {
      if (!this.db) throw new Error('Database not initialized');
      await this.db.delete('keyvaluepairs', key);
    });
  }

  async clear(): Promise<void> {
    return this.executeWithRetry(async () => {
      if (!this.db) throw new Error('Database not initialized');
      await this.db.clear('keyvaluepairs');
      localStorage.clear();
    });
  }

  /**
   * Force reconnection to the database
   * Useful when the app comes back from background on iOS
   */
  async forceReconnect(): Promise<void> {
    console.log('Forcing database reconnection...');
    this.db = null;
    await this.init();
    console.log('Database reconnection completed');
  }
}
